/**
 * 子账户统计 — GET /api/admin/stats/sub-accounts/*
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Col,
  Input,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';

type Scope = 'all' | 'exempt';

interface SubAccountOverview {
  scope: Scope;
  scope_label: string;
  exempt_user_count: number | null;
  total_users: number;
  master_accounts: number;
  sub_accounts: number;
  sub_active: number;
  sub_frozen: number;
  masters_with_subs: number;
  master_orb_total: string;
  master_rp_total: string;
  sub_orb_total: string;
  sub_rp_total: string;
  sub_orb_active: string;
  sub_rp_active: string;
  family_orb_total: string;
  family_rp_total: string;
  subs_with_orb: number;
  subs_with_rp: number;
  avg_sub_orb: string;
  avg_sub_rp: string;
}

interface MasterSubSummaryRow {
  master_id: number;
  master_account: string | null;
  master_number: string | null;
  master_orb: string;
  master_rp: string;
  sub_count: number;
  sub_active: number;
  sub_frozen: number;
  sub_orb_total: string;
  sub_rp_total: string;
  family_orb_total: string;
  family_rp_total: string;
  master_frozen: boolean;
}

interface MasterListResponse {
  total: number;
  rows: MasterSubSummaryRow[];
}

const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

function fmt(value: string | number): string {
  const n = Number(value);
  return Number.isFinite(n) ? numberFormatter.format(n) : String(value);
}

function phpUserLink(accountOrId?: string | number | null): string {
  const q = encodeURIComponent(String(accountOrId ?? ''));
  return `/parity/user_user?kind=master&q=${q}`;
}

export function SubAccountStatsPage() {
  const [scope, setScope] = useState<Scope>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const overviewQuery = useQuery({
    queryKey: ['admin', 'stats', 'sub-accounts', 'overview', scope],
    queryFn: () =>
      api.get<SubAccountOverview>('/api/admin/stats/sub-accounts/overview', {
        params: { scope },
      }),
  });

  const mastersQuery = useQuery({
    queryKey: ['admin', 'stats', 'sub-accounts', 'masters', scope, page, pageSize, search],
    queryFn: () =>
      api.get<MasterListResponse>('/api/admin/stats/sub-accounts/masters', {
        params: {
          scope,
          page,
          limit: pageSize,
          search: search.trim() || undefined,
        },
      }),
  });

  const overview = overviewQuery.data;
  const masters = mastersQuery.data;

  const columns = useMemo(
    () => [
      {
        title: '主号',
        key: 'master',
        render: (_: unknown, row: MasterSubSummaryRow) => (
          <Space direction="vertical" size={0}>
            <Link to={phpUserLink(row.master_account ?? row.master_id)}>
              {row.master_account ?? `#${row.master_id}`}
            </Link>
            {row.master_number ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.master_number}
              </Typography.Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: '状态',
        dataIndex: 'master_frozen',
        width: 80,
        render: (frozen: boolean) =>
          frozen ? <Tag color="red">冻结</Tag> : <Tag color="green">正常</Tag>,
      },
      {
        title: '子账户数',
        dataIndex: 'sub_count',
        width: 100,
        sorter: (a: MasterSubSummaryRow, b: MasterSubSummaryRow) => a.sub_count - b.sub_count,
        defaultSortOrder: 'descend' as const,
      },
      {
        title: '子账户活跃/冻结',
        key: 'sub_status',
        width: 130,
        render: (_: unknown, row: MasterSubSummaryRow) => (
          <span>
            {row.sub_active} / {row.sub_frozen}
          </span>
        ),
      },
      {
        title: '主号 ORB',
        dataIndex: 'master_orb',
        align: 'right' as const,
        render: (v: string) => fmt(v),
      },
      {
        title: '主号 RP',
        dataIndex: 'master_rp',
        align: 'right' as const,
        render: (v: string) => fmt(v),
      },
      {
        title: '子账户 ORB 合计',
        dataIndex: 'sub_orb_total',
        align: 'right' as const,
        render: (v: string) => fmt(v),
      },
      {
        title: '子账户 RP 合计',
        dataIndex: 'sub_rp_total',
        align: 'right' as const,
        render: (v: string) => fmt(v),
      },
      {
        title: '家族 ORB',
        dataIndex: 'family_orb_total',
        align: 'right' as const,
        render: (v: string) => <Typography.Text strong>{fmt(v)}</Typography.Text>,
      },
      {
        title: '家族 RP',
        dataIndex: 'family_rp_total',
        align: 'right' as const,
        render: (v: string) => fmt(v),
      },
    ],
    [],
  );

  const err = overviewQuery.error ?? mastersQuery.error;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          子账户统计
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          按 PHP 镜像 <code>fid</code> 识别子账户；汇总各主号下子账户数量及 ORB/RP 余额。保留区域 =
          玩家编号 100100–100162 及 100193 整条下线。
        </Typography.Paragraph>
      </div>

      <Segmented
        value={scope}
        onChange={(v) => {
          setScope(v as Scope);
          setPage(1);
        }}
        options={[
          { label: '全平台', value: 'all' },
          { label: '保留区域', value: 'exempt' },
        ]}
      />

      {err ? (
        <Alert
          type="error"
          showIcon
          message="加载失败"
          description={err instanceof Error ? err.message : String(err)}
        />
      ) : null}

      {overview ? (
        <>
          <Typography.Text type="secondary">{overview.scope_label}</Typography.Text>
          {overview.exempt_user_count != null ? (
            <Typography.Text type="secondary">
              {' '}
              · 保留区用户数 {numberFormatter.format(overview.exempt_user_count)}
            </Typography.Text>
          ) : null}

          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic title="子账户总数" value={overview.sub_accounts} loading={overviewQuery.isLoading} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="子账户 正常/冻结"
                  value={`${overview.sub_active} / ${overview.sub_frozen}`}
                  loading={overviewQuery.isLoading}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="有子账户的主号"
                  value={overview.masters_with_subs}
                  loading={overviewQuery.isLoading}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic title="主号总数" value={overview.master_accounts} loading={overviewQuery.isLoading} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="子账户 ORB 合计"
                  value={fmt(overview.sub_orb_total)}
                  loading={overviewQuery.isLoading}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="子账户 RP 合计"
                  value={fmt(overview.sub_rp_total)}
                  loading={overviewQuery.isLoading}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="主号 ORB 合计"
                  value={fmt(overview.master_orb_total)}
                  loading={overviewQuery.isLoading}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="主号 RP 合计"
                  value={fmt(overview.master_rp_total)}
                  loading={overviewQuery.isLoading}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="家族 ORB 合计"
                  value={fmt(overview.family_orb_total)}
                  loading={overviewQuery.isLoading}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="家族 RP 合计"
                  value={fmt(overview.family_rp_total)}
                  loading={overviewQuery.isLoading}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="子账户均 ORB"
                  value={fmt(overview.avg_sub_orb)}
                  loading={overviewQuery.isLoading}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="有余额子账户 (ORB/RP)"
                  value={`${overview.subs_with_orb} / ${overview.subs_with_rp}`}
                  loading={overviewQuery.isLoading}
                />
              </Card>
            </Col>
          </Row>
        </>
      ) : null}

      <Card
        title="主号汇总"
        extra={
          <Input.Search
            allowClear
            placeholder="账号 / 玩家编号 / ID"
            style={{ width: 260 }}
            onSearch={(v) => {
              setSearch(v);
              setPage(1);
            }}
          />
        }
      >
        <Table<MasterSubSummaryRow>
          rowKey="master_id"
          size="small"
          loading={mastersQuery.isLoading || mastersQuery.isFetching}
          columns={columns}
          dataSource={masters?.rows ?? []}
          pagination={{
            current: page,
            pageSize,
            total: masters?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (t) => `共 ${t} 个主号`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 1100 }}
        />
      </Card>
    </Space>
  );
}
